/** @format */

import {format, isWeekend, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';

import {generateGraphPointName} from 'helpers/miscHelpers';
import {shortNumber} from 'helpers/numberHelpers';

import {calcMetricForecast} from 'features/mediaplan-progress/helpers/calcMetricForecast';
import {aggregateBag} from 'features/mediaplan-progress/helpers/aggregateBag';
import {
  type ForecastInput,
  type IsoDate,
  type MetricForecast,
  type MetricInput,
} from 'features/mediaplan-progress/types';

import {
  type ChartLabelList,
  type CommonChartData,
  type Chart,
} from 'components/charts/multiline-chart/model/charts';
import {
  type HeightParams,
  type MultilineChartInterface,
  type PointName,
} from 'components/charts/multiline-chart/model/common';
import {type XAxisData} from 'components/charts/multiline-chart/model/xAxis';
import {type TooltipData} from 'components/charts/multiline-chart/model/tooltip';

/**
 * Recharts-ready chart payload consumed by KpiCharts / SpendSection.
 * Mirrors `KpiChartData` / `SpendChartData` (intentionally structural).
 *
 * `axisStart` / `axisEnd` — date labels of the first/last day on the X-axis (period start /
 * end), rendered under the chart (Figma node 10282:123026 calendar).
 * `todayRatio` — today's position along the X-axis (0..1) for the vertical today marker;
 * null when today isn't on the axis. `todayLabel` — today's date label.
 * `valueLabels` — value label at the today point (the metric value today).
 * `forecastValueLabels` — value label on the forecast line (the forecast value at period end).
 */
export interface BuiltChartData<ChartId extends string> {
  data: MultilineChartInterface<ChartId>;
  heightParams: HeightParams;
  axisStart: string;
  axisEnd: string;
  todayRatio: number | null;
  todayLabel: string;
  valueLabels: ChartLabelList;
  forecastValueLabels: ChartLabelList;
}

// Compact height for the overview-card mini chart. It renders as a bare MainChart (no
// MultilineChart x-axis stack / top offset), so the plot fills exactly `boxHeight`. The height
// is supplied by the caller so each chart fills its container: a KPI chart grows with its text
// rows, the Расход chart fills the whole column (see chartHeights.ts). Default 78 = Figma KPI plot.
const DEFAULT_BOX_HEIGHT = 78;

// All HeightParams fields are the chart height — MainChart only uses it via overrideChartHeight,
// and the ChartArea wrapper sizes to overallHeight (no separate calendar/axis strip to add).
const heightParamsFor = (boxHeight: number): HeightParams => ({
  boxHeight,
  containerHeight: boxHeight,
  blockHeight: boxHeight,
  overallHeight: boxHeight,
});

// План на день для аддитивной метрики раскинут по дням периода в buildMetricInput.planDaily;
// для ratio план постоянен. Здесь plan-линия — это просто planDaily накопленный (additive)
// либо константа (ratio).

const sortedKeys = (series: Record<string, number>): IsoDate[] => Object.keys(series).sort();

const toForecastInput = (input: MetricInput): ForecastInput =>
  input.kind === 'additive'
    ? {kind: 'additive', factDaily: input.factDaily, forecastDays: input.forecastDays}
    : {
        kind: 'ratio',
        a: input.ratio.a,
        b: input.ratio.b,
        forecastRange: input.ratio.forecastRange,
        forecastDays: input.forecastDays,
      };

// Per-day факт для ratio — bagPredicate(мешок одного дня).
const ratioFactByDay = (input: Extract<MetricInput, {kind: 'ratio'}>): Record<IsoDate, number> => {
  const result: Record<IsoDate, number> = {};
  input.ratio.factDays.forEach(day => {
    result[day] = input.ratio.bagPredicate(aggregateBag(input.ratio.bagByDay, [day]));
  });
  return result;
};

interface FactPlanSeries {
  // Фактические дни (с фактом), отсортированы.
  factDays: IsoDate[];
  // Значение primary (факт) по дню.
  factByDay: Record<IsoDate, number>;
  // Значение line (план) по дню (для additive — кумулятивный план; для ratio — константа).
  planByDay: Record<IsoDate, number>;
}

const buildFactPlanSeries = (input: MetricInput): FactPlanSeries => {
  if (input.kind === 'additive') {
    const factDays = sortedKeys(input.factDaily);

    // Кумулятивный факт по дням факта (факт режется по today — см. factSeriesFromGraphStats).
    const factByDay: Record<IsoDate, number> = {};
    let factAcc = 0;
    factDays.forEach(day => {
      factAcc += input.factDaily[day] ?? 0;
      factByDay[day] = factAcc;
    });

    // Кумулятивный план копим по ВСЕМ дням периода (planDaily = все дни периода), а не по дням
    // факта — иначе план-линия обрывалась бы на сегодня вместе с фактом. Должна идти до конца.
    const planByDay: Record<IsoDate, number> = {};
    let planAcc = 0;
    sortedKeys(input.planDaily).forEach(day => {
      planAcc += input.planDaily[day] ?? 0;
      planByDay[day] = planAcc;
    });

    return {factDays, factByDay, planByDay};
  }

  // ratio: per-day факт, план — константа planForPeriod. Рисуем план на всех днях оси (факт +
  // прогноз), чтобы целевая линия тянулась до конца периода, а не до последнего дня факта.
  const factByDay = ratioFactByDay(input);
  const factDays = sortedKeys(factByDay);
  const planByDay: Record<IsoDate, number> = {};
  Array.from(new Set([...factDays, ...input.forecastDays])).forEach(day => {
    planByDay[day] = input.planForPeriod;
  });
  return {factDays, factByDay, planByDay};
};

const xAxisDataFor = (day: IsoDate, today: IsoDate): XAxisData => {
  // parseISO reads a date-only string as LOCAL midnight, so date-fns' local format/isWeekend
  // operate on the intended calendar day. `new Date(day)` parses it as UTC midnight, which is the
  // previous day in negative-UTC offsets → day labels and weekend coloring shift by one.
  const date = parseISO(day);
  const result: XAxisData = {
    label: format(date, 'd'),
    color: isWeekend(date) ? '#EC8786' : '#FAFBFE',
  };
  if (day === today) {
    result.todayLabel = format(date, 'd');
  }
  if (date.getDate() === 1) {
    const month = format(date, 'LLLL', {locale: ru});
    result.month = month.slice(0, 1).toUpperCase() + month.slice(1);
  }
  return result;
};

/**
 * Builds a single-series `MultilineChartInterface` from a `MetricInput`:
 *  - fact area (cumulative for additive, per-day for ratio) → `primary`
 *  - plan line (dashed via MultilineChart's `planLineDashed`) → `line`
 *  - forecast tail (average + range from calcMetricForecast) on `forecastDays`
 *
 * `today` is the last day with fact — the boundary between fact and forecast.
 * Returns `null` when there is nothing to plot (no fact and no forecast days).
 */
export const buildChartData = <ChartId extends string>(
  input: MetricInput,
  chartId: ChartId,
  label: string,
  today: IsoDate,
  boxHeight: number = DEFAULT_BOX_HEIGHT,
  // <7 days of fact → no forecast (AS-7886): skip the dashed tail entirely. See shouldShowForecast.
  showForecast: boolean = true,
): BuiltChartData<ChartId> | null => {
  const {factDays, factByDay, planByDay} = buildFactPlanSeries(input);

  const forecastDays = [...input.forecastDays].sort();
  // Все точки по оси X: фактические дни + дни прогноза (без дублей), отсортированы.
  const allDays = Array.from(new Set([...factDays, ...forecastDays])).sort();

  if (allDays.length === 0) {
    return null;
  }

  // calcForecast бросает по контракту, если день прогноза пересекается с днём значения
  // (min(forecastDays) <= max(valueDays)). buildMetricInput уже нормализует forecastDays,
  // но на edge-данных overlap всё ещё возможен — карта НИКОГДА не должна падать из-за прогноза.
  // На исключении деградируем до пустого прогноза: рисуем факт+план без прогнозного хвоста.
  let forecast: MetricForecast = {primary: {}};
  if (showForecast) {
    try {
      forecast = calcMetricForecast(toForecastInput(input));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('buildChartData: forecast skipped due to error', error);
    }
  }
  // Для additive накопительная линия — secondary; для ratio есть только primary.
  const forecastList =
    input.kind === 'additive' ? (forecast.secondary ?? forecast.primary) : forecast.primary;

  // Последняя фактическая кумулята/значение — чтобы прогноз стартовал от факта без разрыва.
  // length>0 → индекс валиден; явная проверка длины вместо `?.` (см. ESLint --fix gotcha).
  const lastFactDay = factDays.length > 0 ? factDays[factDays.length - 1] : null;
  const forecastSeed = lastFactDay !== null ? factByDay[lastFactDay] : null;

  const xAxisDataList: Record<PointName, XAxisData> = {};
  const tooltipDataList: Record<PointName, TooltipData<ChartId>> = {};
  const chartsData: CommonChartData[] = [];

  allDays.forEach((day, index) => {
    const name: PointName = generateGraphPointName(index);
    xAxisDataList[name] = xAxisDataFor(day, today);

    const point: CommonChartData = {name};

    // day может быть прогнозным (нет в factByDay/planByDay) → `in` вместо `!== undefined`
    // (индексный доступ типизирован как number без noUncheckedIndexedAccess, ESLint снял бы проверку).
    if (day in factByDay) {
      point.primary = factByDay[day];
    }
    if (day in planByDay) {
      point.line = planByDay[day];
    }

    // Прогноз рисуем ТОЛЬКО пунктирной линией (point.average → MainChart `<Line average>`),
    // БЕЗ затенённой полосы диапазона. Поэтому `point.range` не заполняем — Area по ключу
    // `range` остаётся без данных и ничего не рендерит (см. AS-7886 / Figma: простой пунктир).
    // При showForecast === false forecastList пуст → хвоста нет; якорь тоже не ставим.
    if (showForecast) {
      const forecastEntry = forecastList[day] as
        | {average: number; range: [number, number]}
        | undefined;
      if (forecastEntry) {
        point.average = forecastEntry.average;
      } else if (day === lastFactDay && forecastSeed !== null) {
        // Якорим линию прогноза к последней фактической точке (общая точка стыка).
        point.average = forecastSeed;
      }
    }

    const tooltipPrimary = point.primary ?? point.average;
    tooltipDataList[name] = {
      date: day,
      rows:
        tooltipPrimary !== undefined
          ? [
              {
                chartId,
                label,
                value: {primary: shortNumber(tooltipPrimary)},
              },
            ]
          : [],
    };

    chartsData.push(point);
  });

  const chart: Chart<ChartId> = {
    chartId,
    label,
    chartsData,
    color: '#547AE0',
    chartHeight: boxHeight,
    skipValueLabels: true,
  };

  // X-axis end labels = period start / end. Figma shows the bare day («1»/«30») for a
  // within-month period, but real flights cross months (e.g. 9 июн → 9 июл where day alone
  // is ambiguous), so we keep the month: «d MMM».
  const axisLabel = (day: IsoDate) => format(parseISO(day), 'd MMM', {locale: ru});

  // The "today" marker sits on the latest fact day that is NOT in the future (the most recent
  // actual reading on/before the calendar `today`). This:
  //  - never falls in the gap that the bare `today` can (derived metrics whose current-day
  //    components aren't reported yet) — the marker/value would otherwise vanish;
  //  - clamps to today instead of the period end when fact is recorded for the whole period
  //    (no forecast, e.g. Расход with Прогноз 0), so the marker stays mid-chart, not at the edge.
  const factDaysUpToToday = factDays.filter(day => day <= today);
  const markerDay =
    factDaysUpToToday.length > 0 ? factDaysUpToToday[factDaysUpToToday.length - 1] : null;
  const todayIndex = markerDay !== null ? allDays.indexOf(markerDay) : -1;
  const todayRatio =
    todayIndex >= 0 && allDays.length > 1 ? todayIndex / (allDays.length - 1) : null;

  // Value labels are nudged left of their data point (Figma «312»/«468» sit left of the line/edge).
  const LABEL_OFFSET_X = 24;

  // Metric value at today, shown at the marker data point (Figma «312»). Primary there =
  // factByDay[markerDay] (cumulative fact for additive, the day's ratio for ratio metrics).
  const valueLabels: ChartLabelList =
    todayIndex >= 0 && markerDay !== null
      ? {
          [generateGraphPointName(todayIndex)]: {
            primary: {show: true, value: factByDay[markerDay], offsetX: LABEL_OFFSET_X},
          },
        }
      : {};

  // Forecast value at period end, shown on the forecast line's last point (Figma «468»).
  // Only label a GENUINE forecast point: the last day must be a forecast day. Otherwise
  // `lastPoint.average` is just the seed anchoring the dashed line to the last fact point
  // (set when there's no forecast tail) — labeling it would print the fact value as a fake
  // forecast, stacking on top of the today value label.
  const lastDay = allDays[allDays.length - 1];
  const isForecastTail = new Set(forecastDays).has(lastDay);
  const lastPoint = chartsData[chartsData.length - 1];
  const forecastValueLabels: ChartLabelList =
    isForecastTail && lastPoint.average !== undefined
      ? {
          [lastPoint.name]: {
            primary: {show: true, value: lastPoint.average, offsetX: LABEL_OFFSET_X},
          },
        }
      : {};

  return {
    data: {
      charts: [chart],
      xAxisDataList,
      tooltipDataList,
    },
    heightParams: heightParamsFor(boxHeight),
    axisStart: axisLabel(allDays[0]),
    axisEnd: axisLabel(allDays[allDays.length - 1]),
    todayRatio,
    todayLabel: todayRatio !== null && markerDay !== null ? axisLabel(markerDay) : '',
    valueLabels,
    forecastValueLabels,
  };
};
